import * as signalR from "@microsoft/signalr";
import { getAppEnvKind } from "@/shared/lib/app-env";
import { handleSilentError } from "@/shared/api/error-handler";

type SignalRConnection = signalR.HubConnection;

let connectionInstance: SignalRConnection | null = null;
let teamConnection: SignalRConnection | null = null;
type TeamEventPayload = unknown;
type TeamEventListener = (payload: TeamEventPayload) => void;

let activeTeamHandlers: {
  TeamUpdated: TeamEventListener;
  TeamMemberAdded: TeamEventListener;
  TeamMemberRemoved: TeamEventListener;
  TeamRoleChanged: TeamEventListener;
  TeamCaptainChanged: TeamEventListener;
} | null = null;
let currentTeamGroup: string | null = null;
let activeSubscriptionsCount = 0;

export function setSignalRConnection(connection: SignalRConnection | null) {
  connectionInstance = connection;
}

export function getSignalRConnection(): SignalRConnection | null {
  return connectionInstance;
}

export function getHubBase(): string {
  const explicit =
    (process.env["NEXT_PUBLIC_HUB_BASE"] as string | undefined) || "";
  if (explicit.trim()) {
    const cleaned = explicit.replace(/\/$/, "");
    if (/^https?:\/\//i.test(cleaned)) {
      if (typeof window === "undefined") return cleaned;
      const isDevLike =
        getAppEnvKind() === "local" || window.location.port === "3000";
      try {
        const u = new URL(cleaned);
        if (!isDevLike && u.origin !== window.location.origin) {
          return `${window.location.origin}/api/hubs`;
        }
      } catch {
        return `${window.location.origin}/api/hubs`;
      }
      return cleaned;
    }
    if (typeof window !== "undefined" && cleaned.startsWith("/")) {
      return `${window.location.origin}${cleaned}`;
    }
    return cleaned;
  }

  if (typeof window !== "undefined") {
    return `${window.location.origin}/api/hubs`;
  }

  return "/api/hubs";
}

export async function updateUserStatusViaSignalR(
  status: string,
  ttlMinutes?: number,
  activityContext?: string | null,
): Promise<boolean> {

  const connection = getSignalRConnection();
  if (!connection) {
    console.warn("[ONLINE] ❌ SignalR соединение не установлено");
    return false;
  }

  if (connection.state !== signalR.HubConnectionState.Connected) {
    console.warn(
      `[ONLINE] ❌ SignalR соединение не в статусе Connected (текущий статус: ${connection.state})`,
    );
    return false;
  }

  const ctx = (activityContext != null && activityContext.trim() !== "") ? activityContext.trim() : null;
  try {
    await connection.invoke("UpdateStatus", status, ttlMinutes ?? null, ctx);
    return true;
  } catch (error) {
    // Тихо для пользователя (статус переотправится), но след остаётся в GlitchTip
    handleSilentError(error, {
      component: "signalr-helpers",
      action: "UpdateStatus",
    });
    return false;
  }
}

export async function setCustomStatusViaSignalR(
  text: string | null,
  emoji: string | null,
  ttlMinutes?: number | null,
): Promise<boolean> {
  const connection = getSignalRConnection();
  if (!connection) {
    console.warn("[ONLINE] ❌ SignalR соединение не установлено (custom status)");
    return false;
  }

  if (connection.state !== signalR.HubConnectionState.Connected) {
    console.warn(
      `[ONLINE] ❌ SignalR соединение не Connected (текущий: ${connection.state}), custom status не отправлен`,
    );
    return false;
  }

  try {
    await connection.invoke(
      "SetCustomStatus",
      text ?? null,
      emoji ?? null,
      ttlMinutes ?? null,
    );
    return true;
  } catch (error) {
    handleSilentError(error, {
      component: "signalr-helpers",
      action: "SetCustomStatus",
    });
    return false;
  }
}

export async function waitForSignalRConnection(
  timeoutMs = 10000,
): Promise<boolean> {
  const start = Date.now();
  while (Date.now() - start < timeoutMs) {
    const conn = getSignalRConnection();
    if (conn && conn.state === signalR.HubConnectionState.Connected) {
      return true;
    }
    await new Promise((r) => setTimeout(r, 500));
  }
  return false;
}

const buildTeamConnection = () => {
  const hubUrl = `${getHubBase()}/team`;
  return new signalR.HubConnectionBuilder()
    .withUrl(hubUrl, {
      withCredentials: true,
    })
    .configureLogging(signalR.LogLevel.None)
    .withAutomaticReconnect()
    .build();
};

async function ensureTeamConnection(): Promise<SignalRConnection | null> {
  if (
    teamConnection &&
    teamConnection.state === signalR.HubConnectionState.Connected
  ) {
    return teamConnection;
  }

  try {
    teamConnection = buildTeamConnection();
    teamConnection.onreconnected(async () => {
      if (currentTeamGroup) {
        try {
          await teamConnection?.invoke("JoinTeamGroup", currentTeamGroup);
        } catch (err) {
          // Reconnect-хук: группа перезайдётся при следующем reconnect
          handleSilentError(err, {
            component: "signalr-helpers",
            action: "JoinTeamGroup.rejoin",
          });
        }
      }
    });
    await teamConnection.start();
    return teamConnection;
  } catch (err) {
    const msg = err instanceof Error ? err.message : String(err);
    if (
      msg.includes("stopped during negotiation") ||
      msg.includes("before stop() was called")
    ) {
      teamConnection = null;
      return null;
    }
    handleSilentError(err, {
      component: "signalr-helpers",
      action: "connectTeamHub",
    });
    teamConnection = null;
    return null;
  }
}

type TeamEventHandlers = {
  onTeamUpdated?: TeamEventListener;
  onMemberAdded?: TeamEventListener;
  onMemberRemoved?: TeamEventListener;
  onRoleChanged?: TeamEventListener;
  onCaptainChanged?: TeamEventListener;
};

const detachTeamHandlers = () => {
  if (!teamConnection || !activeTeamHandlers) return;
  
  try {
    teamConnection.off("TeamUpdated", activeTeamHandlers.TeamUpdated);
    teamConnection.off("TeamMemberAdded", activeTeamHandlers.TeamMemberAdded);
    teamConnection.off("TeamMemberRemoved", activeTeamHandlers.TeamMemberRemoved);
    teamConnection.off("TeamRoleChanged", activeTeamHandlers.TeamRoleChanged);
    teamConnection.off(
      "TeamCaptainChanged",
      activeTeamHandlers.TeamCaptainChanged,
    );
  } catch {
    // Best-effort teardown: соединение уже мертво — снимать нечего
  }
  
  activeTeamHandlers = null;
};

const cleanupTeamConnection = async () => {
  if (activeSubscriptionsCount > 0) return;
  
  try {
    detachTeamHandlers();
    if (teamConnection) {
      await teamConnection.stop();
      teamConnection = null;
      currentTeamGroup = null;
    }
  } catch {
    // Best-effort teardown: ссылки всё равно сбрасываем ниже
    teamConnection = null;
    currentTeamGroup = null;
  }
};

const attachTeamHandlers = (handlers: TeamEventHandlers) => {
  if (!teamConnection) return;
  
  detachTeamHandlers();
  
  const wrap =
    (fn?: TeamEventListener): TeamEventListener =>
    (payload) =>
      fn?.(payload);
  activeTeamHandlers = {
    TeamUpdated: wrap(handlers.onTeamUpdated),
    TeamMemberAdded: wrap(handlers.onMemberAdded),
    TeamMemberRemoved: wrap(handlers.onMemberRemoved),
    TeamRoleChanged: wrap(handlers.onRoleChanged),
    TeamCaptainChanged: wrap(handlers.onCaptainChanged),
  };
  
  try {
    teamConnection.on("TeamUpdated", activeTeamHandlers.TeamUpdated);
    teamConnection.on("TeamMemberAdded", activeTeamHandlers.TeamMemberAdded);
    teamConnection.on("TeamMemberRemoved", activeTeamHandlers.TeamMemberRemoved);
    teamConnection.on("TeamRoleChanged", activeTeamHandlers.TeamRoleChanged);
    teamConnection.on(
      "TeamCaptainChanged",
      activeTeamHandlers.TeamCaptainChanged,
    );
  } catch (err) {
    handleSilentError(err, {
      component: "signalr-helpers",
      action: "attachTeamHandlers",
    });
    activeTeamHandlers = null;
  }
};

export async function subscribeToTeam(
  teamId: string,
  handlers: TeamEventHandlers,
): Promise<() => Promise<void>> {
  if (!teamId) return async () => {};
  
  activeSubscriptionsCount++;
  const conn = await ensureTeamConnection();
  if (!conn) {
    activeSubscriptionsCount--;
    return async () => {};
  }

  attachTeamHandlers(handlers);
  try {
    await conn.invoke("JoinTeamGroup", teamId);
    currentTeamGroup = teamId;
  } catch (err) {
    handleSilentError(err, {
      component: "signalr-helpers",
      action: "JoinTeamGroup",
    });
  }

  return async () => {
    try {
      activeSubscriptionsCount--;
      
      if (currentTeamGroup === teamId) {
        try {
          await conn.invoke("LeaveTeamGroup", teamId);
        } catch (err) {
          // Best-effort: выходим из группы при отписке, хаб всё равно закроется
          handleSilentError(err, {
            component: "signalr-helpers",
            action: "LeaveTeamGroup",
          });
        }
        currentTeamGroup = null;
      }
      
      if (activeSubscriptionsCount === 0) {
        detachTeamHandlers();
        await cleanupTeamConnection();
      }
    } catch (err) {
      handleSilentError(err, {
        component: "signalr-helpers",
        action: "unsubscribeFromTeam",
      });
      activeSubscriptionsCount = Math.max(0, activeSubscriptionsCount - 1);
    }
  };
}
